
import React, { useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Home, FileEdit, Save, Bot, Share2, ScanText } from 'lucide-react';
import { toast } from 'sonner';

interface MobileNavigationProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

const MobileNavigation = ({ activeTab, onTabChange }: MobileNavigationProps) => {
  const { language } = useLanguage();
  
  // Handle connection status changes to enhance mobile experience
  const [isOnline, setIsOnline] = React.useState<boolean>(navigator.onLine);
  
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success(
        language === 'ur' ? 'آپ آن لائن ہیں' : 'You are online',
        { duration: 2000 }
      );
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      toast.error(
        language === 'ur' ? 'آپ آف لائن ہیں' : 'You are offline',
        { duration: 3000 }
      );
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('connectionChange', (e: any) => {
      setIsOnline(e.detail?.online);
    });
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('connectionChange', () => {});
    };
  }, [language]);
  
  const navItems = [
    { 
      id: 'create', 
      icon: <FileEdit size={20} />, 
      label: language === 'ur' ? 'نیا' : 'New',
      ariaLabel: language === 'ur' ? 'نیا دستاویز' : 'New Document'
    },
    { 
      id: 'saved', 
      icon: <Save size={20} />, 
      label: language === 'ur' ? 'محفوظ' : 'Saved',
      ariaLabel: language === 'ur' ? 'محفوظ دستاویزات' : 'Saved Documents'
    },
    { 
      id: 'scanner', 
      icon: <ScanText size={20} />, 
      label: language === 'ur' ? 'اسکینر' : 'Scan',
      ariaLabel: language === 'ur' ? 'دستاویز اسکینر' : 'Document Scanner'
    },
    { 
      id: 'chatbot', 
      icon: <Bot size={20} />, 
      label: language === 'ur' ? 'چیٹ' : 'Chat',
      ariaLabel: language === 'ur' ? 'قانونی چیٹ بوٹ' : 'Legal Chatbot'
    },
    { 
      id: 'shared', 
      icon: <Share2 size={20} />, 
      label: language === 'ur' ? 'اشتراک' : 'Shared',
      ariaLabel: language === 'ur' ? 'اشتراک شدہ دستاویزات' : 'Shared Documents'
    },
  ];

  // Use mobile vibration for tactile feedback
  const handleTabChange = (tab: string) => {
    // Skip if it's the current tab
    if (tab === activeTab) return;
    
    // Provide haptic feedback if available
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
    
    onTabChange(tab);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 shadow-lg border-t border-gray-200 dark:border-gray-800 z-50 ios-fixed-fix force-gpu">
      {/* Connection status indicator - only show when offline */}
      {!isOnline && (
        <div className="w-full bg-red-500 text-white text-center text-xs py-1">
          {language === 'ur' ? 'آپ آف لائن ہیں۔ انٹرنیٹ کنکشن چیک کریں' : 'You are offline. Check your internet connection.'}
        </div>
      )}
      
      <div className="flex justify-around items-center h-16">
        {navItems.map((item) => (
          <Button
            key={item.id}
            variant="ghost"
            aria-label={item.ariaLabel}
            className={`flex flex-col items-center justify-center py-2 px-4 rounded-none h-full w-full transition-all ${
              activeTab === item.id 
                ? 'bg-blue-50 text-primary border-t-2 border-blue-500 dark:bg-gray-800 dark:border-blue-400' 
                : 'text-gray-500 dark:text-gray-400'
            }`}
            onClick={() => handleTabChange(item.id)}
          >
            {item.icon}
            <span className="text-xs mt-1">{item.label}</span>
          </Button>
        ))}
      </div>
    </div>
  );
};

export default MobileNavigation;
